import { bridge, getMemberJWTTokenKey, getWebBaseURL, isApp } from '../utils';
import { AxiosError, HttpStatusCode } from 'axios';
import React from 'react';
import { Image, Pressable, View } from 'react-native';
import cookie from 'js-cookie';
import { ColorValues, Typography } from '@banhala/ui-kit';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NavigationProp, NavigationState } from '@react-navigation/native';
import RNRestart from 'react-native-restart';
import { moveToToday } from '../utils/navigator/moveToToday';

interface ErrorBoundaryProps extends React.ComponentProps<'div'> {
  showRefreshButton?: boolean;
  title?: string;
  ErrorFallback?: (e: Error) => React.ReactNode;
  instanceId?: string;
  navigation?: Omit<
    NavigationProp<ReactNavigation.RootParamList>,
    'getState'
  > & {
    getState(): NavigationState | undefined;
  };
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  {
    statusCode?: number;
    descriptions: string[];
    error?: Error;
  }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      statusCode: undefined,
      descriptions: [],
    };
  }

  static getDerivedStateFromError(error: Error) {
    if (error instanceof AxiosError) {
      return {
        serverError: true,
        statusCode: error.status,
        descriptions: [error.message],
        error,
      };
    }

    return { error };
  }

  onGoHome() {
    moveToToday();
  }

  async onRefresh(statusCode?: number) {
    if (statusCode === HttpStatusCode.Unauthorized && !isApp()) {
      cookie.remove(await getMemberJWTTokenKey());
      const baseURL = await getWebBaseURL();
      window.location.replace(
        `${baseURL}/login?redirect=${window.location.href}`,
      );
      return;
    }

    if (isApp()) {
      bridge.closeInstance({ instanceId: this.props.instanceId });
      return;
    }

    window.location.reload();
  }

  handleClick(statusCode?: number) {
    try {
      if (!__DEV__) RNRestart.restart();
    } catch {
      /* empty */
    }

    if (statusCode === HttpStatusCode.NotFound) {
      this.onGoHome();
      return;
    }

    this.onRefresh(statusCode);
  }

  render() {
    const {
      showRefreshButton = true,
      title = '오류가 발생했어요',
      children,
      ErrorFallback,
    } = this.props;
    const { error, statusCode, descriptions } = this.state;
    if (error === undefined) return children;

    let pageTitle = title;
    let buttonText = '새로고침';

    if (statusCode === HttpStatusCode.NotFound) {
      pageTitle = '페이지를 찾을 수 없어요';
      buttonText = '홈으로 이동';
    }

    if (ErrorFallback && error) {
      return ErrorFallback(error);
    }
    return (
      <SafeAreaView>
        <View
          style={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Image
            style={{
              width: 156,
              height: 156,
            }}
            source={{
              uri: 'https://img.a-bly.com/common/img-error@3x.png',
            }}
            alt="에러 이미지"
          />
          <Typography type="h3" marginBottom={16}>
            {pageTitle}
          </Typography>
          {[...descriptions].map(description => (
            <Typography
              key={description}
              type="body1"
              color="content_secondary"
            >
              {description}
            </Typography>
          ))}
          {showRefreshButton && (
            // TODO: AblyButton으로 교체
            <Pressable
              style={{
                marginTop: 36,
                display: 'flex',
                height: 56,
                width: 200,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                backgroundColor: ColorValues.black,
              }}
              onPress={() => this.handleClick(statusCode)}
            >
              <Typography type="h5" color="content_always_white">
                {buttonText}
              </Typography>
            </Pressable>
          )}
        </View>
      </SafeAreaView>
    );
  }
}
